import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import sprite from '../../../assets/icons/sprite.svg';
import { UserContext } from '../../../context';
import {
  ChannelRole,
  OrganisationRole,
  OrganisationUserOrderBy,
  UserRole,
} from '../../../services';
import {
  Avatar,
  getAriaSort,
  getChannelDomain,
  getCssVar,
  getWebAppUrl,
  IconLabel,
  OrderDirection,
  SearchField,
  useAppDispatch,
  useAppSelector,
  useDebounce,
  useIsNarrowViewport,
  useRestoreFocusAfterFetch,
} from '../../../shared';
import {
  getOrganisationUsers,
  organisationAdminRemove,
  organisationAdminUpdate,
} from '../../../slices';

import classNames from 'classnames';
import classes from './Administration.module.scss';

interface AdministrationUsersArgs {
  page: number;
  query?: string;
  orderBy?: OrganisationUserOrderBy;
  orderDirection?: OrderDirection;
}

interface OrganisationAdminChangeArgs {
  id: number;
  isOrgAdmin: boolean;
}

interface SortHeaderArgs {
  orderBy: OrganisationUserOrderBy;
  label: string;
}

export const Administration: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardAdministration' });

  const dispatch = useAppDispatch();

  const isNarrowViewport = useIsNarrowViewport();

  const userId = useContext(UserContext).userProfile.id;

  const [orderByActive, setOrderByActive] = useState<OrganisationUserOrderBy>(
    OrganisationUserOrderBy.SCREEN_NAME
  );

  const [orderDirectionActive, setOrderDirectionActive] = useState<OrderDirection>(
    OrderDirection.ASC
  );

  const [search, setSearch] = useState<string>('');

  const debouncedSearch = useDebounce<string>(search);

  const { isFetched, users, pageInfo } = useAppSelector(
    ({ organisationUsers }) => organisationUsers
  );

  const restoreFocusAfterFetch = useRestoreFocusAfterFetch(isFetched);

  const tableRef = useRef<HTMLTableElement>(null);

  const previousPageRef = useRef<HTMLButtonElement>(null);

  const nextPageRef = useRef<HTMLButtonElement>(null);

  const getAdministrationUsers = useCallback(
    ({
      page,
      query = search,
      orderBy = orderByActive,
      orderDirection = orderDirectionActive,
    }: AdministrationUsersArgs) => {
      dispatch(
        getOrganisationUsers({
          page,
          orderBy,
          orderDirection,
          filters: {
            query,
            rolesId: [ChannelRole.ADMIN, OrganisationRole.ADMIN],
            excludeBanned: true,
          },
        })
      );
    },
    [dispatch, orderByActive, orderDirectionActive, search]
  );

  useEffect(
    () => getAdministrationUsers({ page: 1 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch, orderByActive, orderDirectionActive]
  );

  const onNameClick = useCallback(
    (id: number) => {
      const url = `${getWebAppUrl(getChannelDomain())}/admin${
        id === userId ? '/profile' : `/profile/${id}`
      }`;

      window.open(url, '_blank', 'noopener, noreferrer');
    },
    [userId]
  );

  const adminRoleChangeHandler = useCallback(
    async ({ id, isOrgAdmin }: OrganisationAdminChangeArgs) => {
      try {
        if (isOrgAdmin) {
          await dispatch(organisationAdminRemove({ userIds: [id] }));
          return;
        }
        await dispatch(
          organisationAdminUpdate({ userIds: [id], role: UserRole.ORGANISATION_ADMIN })
        );
      } catch (_) {
        toast.error(t('common.error-title'));
      } finally {
        getAdministrationUsers({ page: pageInfo.page });
      }
    },
    [dispatch, getAdministrationUsers, pageInfo.page, t]
  );

  const sortBy = useCallback(
    (orderBy: OrganisationUserOrderBy) => {
      const orderDirection =
        orderBy !== orderByActive
          ? OrderDirection.ASC
          : orderDirectionActive === OrderDirection.DESC
          ? OrderDirection.ASC
          : OrderDirection.DESC;

      restoreFocusAfterFetch(() =>
        tableRef.current?.querySelector<HTMLElement>('th[aria-sort] button')
      );

      setOrderByActive(orderBy);
      setOrderDirectionActive(orderDirection);
    },
    [orderByActive, orderDirectionActive, restoreFocusAfterFetch]
  );

  const sortHeader = useCallback(
    ({ orderBy, label }: SortHeaderArgs) => {
      const isSorted = orderBy === orderByActive;

      return (
        <th {...(isSorted && { 'aria-sort': getAriaSort(orderDirectionActive) })}>
          <button
            type={'button'}
            className={classes['administration__table-sort-header']}
            onClick={() => sortBy(orderBy)}
          >
            {label}
            {isSorted && (
              <svg className={classes['administration__table-sort']}>
                <use
                  href={`${sprite}#${
                    orderDirectionActive === OrderDirection.DESC ? 'down' : 'up'
                  }-arrow`}
                />
              </svg>
            )}
          </button>
        </th>
      );
    },
    [orderByActive, orderDirectionActive, sortBy]
  );

  const pageBackHandler = useCallback(() => {
    restoreFocusAfterFetch(() =>
      previousPageRef.current?.disabled ? nextPageRef.current : previousPageRef.current
    );

    getAdministrationUsers({ page: pageInfo.page - 1 });
  }, [getAdministrationUsers, pageInfo.page, restoreFocusAfterFetch]);

  const pageForwardHandler = useCallback(() => {
    restoreFocusAfterFetch(() =>
      nextPageRef.current?.disabled ? previousPageRef.current : nextPageRef.current
    );

    getAdministrationUsers({ page: pageInfo.page + 1 });
  }, [getAdministrationUsers, pageInfo.page, restoreFocusAfterFetch]);

  const pagination = useMemo(() => {
    const { page, perPage, hasNextPage, hasPreviousPage, totalItems } = pageInfo;

    if (!totalItems) {
      return null;
    }

    const from = (page - 1) * perPage + 1;

    const to = Math.min(page * perPage, totalItems);

    return (
      <div className={classes['administration__pagination']}>
        <IconLabel
          ref={previousPageRef}
          iconSize={20}
          iconId={'arrow-left'}
          ariaLabel={t('common.previous-page')}
          disabled={!hasPreviousPage}
          onClick={pageBackHandler}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
        />

        <span className={classes['administration__pagination-label']}>
          {from}-{to} {t('common.of')} {totalItems}
        </span>

        <IconLabel
          ref={nextPageRef}
          iconSize={20}
          iconId={'arrow-right'}
          ariaLabel={t('common.next-page')}
          disabled={!hasNextPage}
          onClick={pageForwardHandler}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
        />
      </div>
    );
  }, [pageBackHandler, pageForwardHandler, pageInfo, t]);

  const table = useMemo(() => {
    if (!isFetched) {
      return (
        <div className={classes['administration__loader']}>
          <Skeleton height={'3rem'} />
          <Skeleton height={'3rem'} />
          <Skeleton height={'3rem'} />
        </div>
      );
    }

    return (
      <>
        <div
          className={classNames(classes['administration__table-wrapper'], {
            [classes['administration__table-wrapper--narrow']]: isNarrowViewport,
          })}
        >
          <table
            ref={tableRef}
            className={classNames(classes['administration__table'], {
              [classes['administration__table--narrow']]: isNarrowViewport,
            })}
          >
            <colgroup>
              <col width={'30%'} />
              <col width={'30%'} />
              <col width={'20%'} />
              <col width={'20%'} />
            </colgroup>
            <thead>
              <tr>
                {sortHeader({
                  orderBy: OrganisationUserOrderBy.SCREEN_NAME,
                  label: t('common.name'),
                })}
                {sortHeader({ orderBy: OrganisationUserOrderBy.EMAIL, label: t('common.email') })}
                <th>{t('common.role')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map(({ id, screenName, email, roleId, avatar }) => {
                const isOrgAdmin = roleId === UserRole.ORGANISATION_ADMIN;

                return (
                  <tr key={id}>
                    <td>
                      <button
                        type={'button'}
                        className={classes['administration__table-name-wrapper']}
                        onClick={() => onNameClick(id)}
                      >
                        <Avatar url={avatar?.url} />
                        <span className={classes['administration__table-name']}>{screenName}</span>
                      </button>
                    </td>
                    <td>
                      <div className={classes['administration__table-email']}>{email}</div>
                    </td>
                    <td>{t(`userRoles.${roleId}`)}</td>
                    <td className={classes['administration__table-action']}>
                      {roleId !== UserRole.ORGANISATION_OWNER && (
                        <IconLabel
                          label={ctxT(`${isOrgAdmin ? 'revoke' : 'make'}-org-admin`)}
                          onClick={() => adminRoleChangeHandler({ id, isOrgAdmin })}
                          color={getCssVar('--base-link-text-color')}
                          hoverColor={getCssVar('--base-link-text-hover-color')}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!Boolean(users.length) && (
          <div className={classes['administration__empty']}>{ctxT('no-users-found')}</div>
        )}
        {pagination}
      </>
    );
  }, [
    adminRoleChangeHandler,
    ctxT,
    isFetched,
    isNarrowViewport,
    onNameClick,
    pagination,
    sortHeader,
    t,
    users,
  ]);

  return (
    <div className={classes['administration']}>
      <h1 className={classes['administration__title']}>{t('orgLayoutMenu.administration')}</h1>
      <div className={classes['administration__intro']}>{ctxT('intro')}</div>
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t('common.type-to-search')}
        className={classes['administration__search']}
      />
      {table}
    </div>
  );
};
